import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../config/firebase';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import industryIcon from '../../assets/icons/industry.png';
import productIcon from '../../assets/icons/product.png';
import bizModelIcon from '../../assets/icons/biz_model.gif';
import stageIcon from '../../assets/icons/stage.gif';
import amountIcon from '../../assets/money_received_2.png';
import locationIcon from '../../assets/icons/location.png';
import foundersIcon from '../../assets/icons/founders.png';
import handshakeIcon from '../../assets/handshake__white_.png';
import trophyIcon from '../../assets/trophy.png';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import {
  industrySelectOptions,
  canonicalIndustryList,
  INDUSTRY_OTHER_VALUE,
  productTypeSelectOptions,
  stageSelectOptions,
} from '../../constants/industry';
import {
  investorInvestmentRangeFloor,
  investmentRangeIsSaveable,
} from '../../utils/investmentRangeEdit';
import {
  INDUSTRY_DISPLAY_MAP,
  BUSINESS_MODEL_DISPLAY_MAP,
  PRODUCT_FOCUS_DISPLAY_MAP,
  STAGE_DISPLAY_MAP,
  getDisplayName,
} from '../../constants/display-mappings';
import Checkbox from '../Checkbox';
import RangeSelector from './steps/RangeSelector';
import { CountryRegionSelector } from './steps/CountryRegionSelector';
import { countriesToSave, usStates } from '../../constants/countries';
import ExpressSignupSuccessPopup from './ExpressSignupSuccessPopup';
import CountrySelect from '../CountrySelect';
import { countryToIso2 } from '../../utils/countryFlag';
import {
  MAX_INVESTMENT_AMOUNT_USD,
  MIN_INVESTMENT_AMOUNT,
} from '../../constants/profileFieldLimits';
import { maxInvestmentInCurrency } from '../../utils/minStartupAsk';
import { PRAIRI_SURFACES } from '../../constants/prairiSurfaces';
import { SUCCESS_FEE_LIVE } from '../../constants/successFee';
import { loadPricingSignupDraft } from '../../utils/pricingSignupDraft';
import {
  expressAccountBindAction,
  expressContextUid,
  investorProfileBelongsToAccount,
} from '../../utils/signupSessionGuards';
import {
  countryRequiresStateSubdivision,
  isUnitedKingdomCountry,
  isUnitedStatesCountry,
} from '../../utils/investorPricingView';
import { UK_COUNTIES } from '../../constants/ukLocations';
import CitySelect from './steps/CitySelect';
import { unionStringLists } from '../../utils/unionStringLists';

// Generic modal component for checkbox selections
interface CheckboxModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedValues: string[];
  onSave: (values: string[]) => void;
  title: string;
  options: Array<{ name: string; value: string }>;
  /** Founder representation can be left empty. That means no preference. */
  allowEmpty?: boolean;
}

const CheckboxModal: React.FC<CheckboxModalProps> = ({
  isOpen,
  onClose,
  selectedValues,
  onSave,
  title,
  options,
  allowEmpty = false,
}) => {
  const [localSelected, setLocalSelected] = useState<string[]>(selectedValues);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setLocalSelected(selectedValues);
      setError(null);
    }
  }, [isOpen, selectedValues, options]);

  const handleToggle = (value: string) => {
    setLocalSelected(prev => {
      const newSelected = prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value];
      // Clear error when user makes a selection
      if (newSelected.length > 0) {
        setError(null);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (localSelected.length === options.length) {
      setLocalSelected([]);
    } else {
      setLocalSelected(options.map(opt => opt.value));
      setError(null); // Clear error when selecting all
    }
  };

  const handleClose = () => {
    if (!allowEmpty && localSelected.length === 0) {
      setError('Please select at least one option');
      return;
    }
    setError(null);
    onClose();
  };

  const handleSave = () => {
    if (!allowEmpty && localSelected.length === 0) {
      setError('Please select at least one option');
      return;
    }
    setError(null);
    onSave(localSelected);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-dark-950/95 flex items-center justify-center z-[100000]">
      <div className="bg-dark-900 rounded-2xl p-8 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-white uppercase">{title}</h2>
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-white text-sm hover:text-emerald-400 transition-colors"
            >
              {localSelected.length === options.length ? 'UNSELECT ALL' : 'SELECT ALL'}
            </button>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white text-2xl">
            ×
          </button>
        </div>

        <div className="space-y-3 mb-6">
          {options.map(option => (
            <Checkbox
              key={option.value}
              id={`modal-${title}-${option.value}`}
              checked={localSelected.includes(option.value)}
              onChange={() => handleToggle(option.value)}
              label={option.name}
            />
          ))}
        </div>

        <div className="relative mb-4 min-h-[24px]">
          {error && <p className="text-red-500 text-sm absolute top-0 left-0">{error}</p>}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-white border border-gray-600 rounded-lg hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

interface IndustriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedValues: string[];
  otherIndustry: string;
  onSave: (values: string[], otherIndustry: string) => void;
  options: Array<{ name: string; value: string }>;
}

const IndustriesModal: React.FC<IndustriesModalProps> = ({
  isOpen,
  onClose,
  selectedValues,
  otherIndustry,
  onSave,
  options,
}) => {
  const [localSelected, setLocalSelected] = useState<string[]>(selectedValues);
  const [localOtherIndustry, setLocalOtherIndustry] = useState(otherIndustry);
  const [error, setError] = useState<string | null>(null);
  const [otherError, setOtherError] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setLocalSelected(selectedValues);
      setLocalOtherIndustry(otherIndustry);
      setError(null);
      setOtherError(false);
    }
  }, [isOpen, selectedValues, otherIndustry, options]);

  const handleToggle = (value: string) => {
    setLocalSelected(prev => {
      const newSelected = prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value];
      if (newSelected.length > 0) {
        setError(null);
      }
      if (!newSelected.includes(INDUSTRY_OTHER_VALUE)) {
        setLocalOtherIndustry('');
        setOtherError(false);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    const standardValues = options.map(opt => opt.value);
    const allStandardSelected = standardValues.every(value => localSelected.includes(value));
    if (allStandardSelected) {
      setLocalSelected(prev => prev.filter(value => value === INDUSTRY_OTHER_VALUE));
    } else {
      setLocalSelected(prev => {
        const keptOther = prev.includes(INDUSTRY_OTHER_VALUE) ? [INDUSTRY_OTHER_VALUE] : [];
        return [...standardValues, ...keptOther];
      });
      setError(null);
    }
  };

  const validate = (): boolean => {
    if (localSelected.length === 0) {
      setError('Please select at least one industry');
      return false;
    }
    if (localSelected.includes(INDUSTRY_OTHER_VALUE) && !localOtherIndustry.trim()) {
      setError('Please specify your other industry');
      setOtherError(true);
      return false;
    }
    setError(null);
    setOtherError(false);
    return true;
  };

  const handleClose = () => {
    if (!validate()) return;
    onClose();
  };

  const handleSave = () => {
    if (!validate()) return;
    const trimmedOther = localOtherIndustry.trim();
    onSave(localSelected, localSelected.includes(INDUSTRY_OTHER_VALUE) ? trimmedOther : '');
    onClose();
  };

  const standardValues = options.map(opt => opt.value);
  const allStandardSelected = standardValues.every(value => localSelected.includes(value));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-dark-950/95 flex items-center justify-center z-[100000]">
      <div className="bg-dark-900 rounded-2xl p-8 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-white uppercase">INDUSTRIES</h2>
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-white text-sm hover:text-emerald-400 transition-colors"
            >
              {allStandardSelected ? 'UNSELECT ALL' : 'SELECT ALL'}
            </button>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white text-2xl">
            ×
          </button>
        </div>

        <div className="space-y-3 mb-6">
          {options.map(option => (
            <Checkbox
              key={option.value}
              id={`modal-industries-${option.value}`}
              checked={localSelected.includes(option.value)}
              onChange={() => handleToggle(option.value)}
              label={option.name}
            />
          ))}

          <div className="flex items-center gap-3 pt-1">
            <Checkbox
              id="modal-industries-other"
              checked={localSelected.includes(INDUSTRY_OTHER_VALUE)}
              onChange={() => handleToggle(INDUSTRY_OTHER_VALUE)}
              label="Other"
              hasError={otherError}
            />
            <input
              type="text"
              value={localOtherIndustry}
              onChange={e => {
                setLocalOtherIndustry(e.target.value);
                if (e.target.value.trim()) {
                  setOtherError(false);
                  setError(null);
                }
              }}
              disabled={!localSelected.includes(INDUSTRY_OTHER_VALUE)}
              placeholder="Please specify"
              aria-label="Other industry"
              className={`flex-1 min-w-0 border-b bg-transparent px-1 py-1 text-sm text-white placeholder:text-gray-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
                otherError
                  ? 'border-red-500 text-red-100'
                  : 'border-gray-500 focus:border-emerald-400'
              }`}
            />
          </div>
        </div>

        <div className="relative mb-4 min-h-[24px]">
          {error && <p className="text-red-500 text-sm absolute top-0 left-0">{error}</p>}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-white border border-gray-600 rounded-lg hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Business Model Modal - Special handling: only B2B and B2C, save as "b2b-b2c" if both selected
interface BusinessModelModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedValues: string[];
  onSave: (values: string[]) => void;
}

const BusinessModelModal: React.FC<BusinessModelModalProps> = ({
  isOpen,
  onClose,
  selectedValues,
  onSave,
}) => {
  // Only B2B and B2C options
  const options = React.useMemo(
    () => [
      { name: 'B2B', value: 'b2b' },
      { name: 'B2C', value: 'b2c' },
    ],
    []
  );

  // Convert saved value to local state
  // If "b2b-b2c" is in selectedValues, both should be selected
  // If only one is in selectedValues, that one should be selected
  const getInitialState = (): string[] => {
    if (selectedValues.length === 0) {
      return [];
    }
    if (selectedValues.includes('b2b-b2c')) {
      return ['b2b', 'b2c'];
    }
    return selectedValues.filter(v => v === 'b2b' || v === 'b2c');
  };

  const [localSelected, setLocalSelected] = useState<string[]>(getInitialState);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      if (selectedValues.length === 0) {
        setLocalSelected([]);
      } else if (selectedValues.includes('b2b-b2c')) {
        setLocalSelected(['b2b', 'b2c']);
      } else {
        setLocalSelected(selectedValues.filter(v => v === 'b2b' || v === 'b2c'));
      }
      setError(null);
    }
  }, [isOpen, selectedValues, options]);

  const handleToggle = (value: string) => {
    setLocalSelected(prev => {
      const newSelected = prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value];
      // Clear error when user makes a selection
      if (newSelected.length > 0) {
        setError(null);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (localSelected.length === options.length) {
      setLocalSelected([]);
    } else {
      setLocalSelected(options.map(opt => opt.value));
      setError(null); // Clear error when selecting all
    }
  };

  const handleClose = () => {
    if (localSelected.length === 0) {
      setError('Please select at least one option');
      return;
    }
    setError(null);
    onClose();
  };

  const handleSave = () => {
    if (localSelected.length === 0) {
      setError('Please select at least one option');
      return;
    }
    setError(null);
    // If both selected, save as "b2b-b2c", otherwise save the array
    if (localSelected.length === 2) {
      onSave(['b2b-b2c']);
    } else {
      onSave(localSelected);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-dark-950/95 flex items-center justify-center z-[100000]">
      <div className="bg-dark-900 rounded-2xl p-8 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-white uppercase">BUSINESS MODEL PREFERENCE</h2>
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-white text-sm hover:text-emerald-400 transition-colors"
            >
              {localSelected.length === options.length ? 'UNSELECT ALL' : 'SELECT ALL'}
            </button>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white text-2xl">
            ×
          </button>
        </div>

        <div className="space-y-3 mb-6">
          {options.map(option => (
            <Checkbox
              key={option.value}
              id={`modal-business-model-${option.value}`}
              checked={localSelected.includes(option.value)}
              onChange={() => handleToggle(option.value)}
              label={option.name}
            />
          ))}
        </div>

        <div className="relative mb-4 min-h-[24px]">
          {error && <p className="text-red-500 text-sm absolute top-0 left-0">{error}</p>}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-white border border-gray-600 rounded-lg hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Location Modal
interface LocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCountries: string[];
  onSave: (countries: string[]) => void;
}

const LocationModal: React.FC<LocationModalProps> = ({
  isOpen,
  onClose,
  selectedCountries,
  onSave,
}) => {
  const concreteInitial = selectedCountries.filter(country => country !== 'ANYWHERE');
  const hasAnywhereInitial = concreteInitial.length === 0 && selectedCountries.includes('ANYWHERE');
  const [localSelectedCountries, setLocalSelectedCountries] = useState<string[]>(concreteInitial);
  const [localSelectedRegions, setLocalSelectedRegions] = useState<string[]>([]);
  const [isAnywhereSelected, setIsAnywhereSelected] = useState<boolean>(hasAnywhereInitial);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const concrete = selectedCountries.filter(country => country !== 'ANYWHERE');
    setIsAnywhereSelected(concrete.length === 0 && selectedCountries.includes('ANYWHERE'));
    setLocalSelectedCountries(concrete);
    setLocalSelectedRegions([]);
    setError(null);
    // Snapshot the saved list only when the dialog opens. A later parent render
    // must not put Worldwide back over the country the investor just picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    // Check if there's at least one selection (ANYWHERE counts)
    if (!isAnywhereSelected && localSelectedCountries.length === 0) {
      setError('Please select at least one location');
      return;
    }
    setError(null);
    onClose();
  };

  const handleSave = () => {
    const next = countriesToSave(localSelectedCountries, isAnywhereSelected);
    if (next.length === 0) {
      setError('Please select at least one location');
      return;
    }
    setError(null);
    onSave(next);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-dark-950/95 flex items-center justify-center z-[100000]">
      <div className="bg-dark-900 rounded-2xl p-8 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white uppercase">LOCATION</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-white text-2xl">
            ×
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-300 mb-4">Where would you prefer the startups to be located?</p>
          <CountryRegionSelector
            selectedCountries={localSelectedCountries}
            selectedRegions={localSelectedRegions}
            isAnywhereSelected={isAnywhereSelected}
            onCountriesChange={countries => {
              setLocalSelectedCountries(countries);
              // Clear error when user makes a selection
              if (countries.length > 0 || isAnywhereSelected) {
                setError(null);
              }
            }}
            onRegionsChange={regions => {
              setLocalSelectedRegions(regions);
              // Clear error when user makes a selection
              if (regions.length > 0 || isAnywhereSelected || localSelectedCountries.length > 0) {
                setError(null);
              }
            }}
            onAnywhereChange={anywhere => {
              setIsAnywhereSelected(anywhere);
              setError(null); // Clear error when selecting ANYWHERE
            }}
          />
        </div>

        <div className="relative mb-4 min-h-[24px]">
          {error && <p className="text-red-500 text-sm absolute top-0 left-0">{error}</p>}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-white border border-gray-600 rounded-lg hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Success Fee Modal
interface SuccessFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentValue: boolean | null;
  onSave: (value: boolean) => void;
}

const SuccessFeeModal: React.FC<SuccessFeeModalProps> = ({
  isOpen,
  onClose,
  currentValue,
  onSave,
}) => {
  const [localValue, setLocalValue] = useState<boolean | null>(currentValue);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setLocalValue(currentValue);
      setError(null);
    }
  }, [isOpen, currentValue]);

  const handleClose = () => {
    if (localValue === null) {
      setError('Please select an option');
      return;
    }
    setError(null);
    onClose();
  };

  const handleSave = () => {
    if (localValue === null) {
      setError('Please select an option');
      return;
    }
    setError(null);
    onSave(localValue);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-dark-950/95 flex items-center justify-center z-[100000]">
      <div className="bg-dark-900 rounded-2xl p-8 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white uppercase">SUCCESS FEE</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-white text-2xl">
            ×
          </button>
        </div>

        <p className="text-gray-300 mb-6">
          For each investment made into a startup discovered through PRAIRI (unless they’ve pitched
          me in the last 6 months), the 2% success fee will be paid by:
        </p>

        <div className="space-y-6 mb-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="radio"
              name="successFee"
              checked={localValue === true}
              onChange={() => {
                setLocalValue(true);
                setError(null);
              }}
              className="mt-1 w-5 h-5 text-emerald-500 border-gray-600 focus:ring-emerald-500"
            />
            <div className="min-w-0 flex-1 text-left">
              <span className="text-white text-lg">Separately by us </span>
              <span className="text-emerald-400 text-sm italic">
                (preferred by highly sought-after startups)
              </span>
              <p className="mt-3 text-sm text-gray-400">
                Displayed to the startup when you message them:
              </p>
              <div
                style={{ backgroundColor: PRAIRI_SURFACES.profileCard }}
                className={`mt-2 flex w-full max-w-[21rem] items-center space-x-2.5 rounded-xl border p-3 text-left transition-colors ${
                  localValue === true ? 'border-[#0ea5e9]' : 'border-gray-600/60'
                }`}
              >
                <span className="text-[#92d050] text-base leading-none flex-shrink-0">★</span>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-bold break-words">
                    Startup-Friendly Investor
                  </p>
                  <p className="mt-0.5 text-xs text-gray-300 leading-snug break-words">
                    This investor has generously agreed to pay PRAIRI&apos;s &apos;success fee&apos;
                    from their own funds.
                  </p>
                </div>
              </div>
            </div>
          </label>

          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="radio"
              name="successFee"
              checked={localValue === false}
              onChange={() => {
                setLocalValue(false);
                setError(null);
              }}
              className="mt-1 w-5 h-5 text-emerald-500 border-gray-600 focus:ring-emerald-500"
            />
            <div className="min-w-0 flex-1 text-left">
              <span className="text-white text-lg">From the startup&apos;s allocation</span>
              <p className="mt-3 text-sm text-gray-400">
                Displayed to the startup when you message them:
              </p>
              <div
                style={{ backgroundColor: PRAIRI_SURFACES.profileCard }}
                className={`mt-2 flex w-full max-w-[21rem] items-start space-x-2.5 rounded-xl border p-3 text-left transition-colors ${
                  localValue === false ? 'border-[#0ea5e9]' : 'border-gray-600/60'
                }`}
              >
                <span className="text-amber-400 text-xs leading-none mt-1 flex-shrink-0">●</span>
                <p className="text-xs text-gray-300 leading-snug break-words">
                  PRAIRI&apos;s &apos;success fee&apos; (2% of this investor&apos;s total
                  investment) will be allocated from the amount they provide you.
                </p>
              </div>
            </div>
          </label>
        </div>

        <div className="relative mb-4 min-h-[24px]">
          {error && <p className="text-red-500 text-sm absolute top-0 left-0">{error}</p>}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-white border border-gray-600 rounded-lg hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Notifications Modal — Alice UX: Send Verification Code → enter OTP in popup
interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  initialSmsEnabled: boolean;
  initialPhone: string;
  phoneCountry: string;
  onSave: (smsEnabled: boolean, phone: string) => void;
}

const NotificationsModal: React.FC<NotificationsModalProps> = ({
  isOpen,
  onClose,
  userEmail,
  initialSmsEnabled,
  initialPhone,
  phoneCountry,
  onSave,
}) => {
  const { user, refreshUser } = useAuth();
  const [localSmsEnabled, setLocalSmsEnabled] = useState(initialSmsEnabled);
  const [localPhone, setLocalPhone] = useState(initialPhone);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState<'idle' | 'send' | 'verify'>('idle');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const formatPhone = (value: string) => (value.startsWith('+') ? value : `+${value}`);

  React.useEffect(() => {
    if (!isOpen) return;
    setLocalSmsEnabled(initialSmsEnabled || Boolean(user?.phoneVerified));
    setLocalPhone(initialPhone || user?.phone || '');
    setPhoneVerified(Boolean(user?.phoneVerified));
    setCode('');
    setCodeSent(false);
    setPhoneError(null);
    setInfo(null);
    setBusy('idle');
  }, [isOpen, initialSmsEnabled, initialPhone, user?.phone, user?.phoneVerified]);

  const handleSendCode = async () => {
    setPhoneError(null);
    setInfo(null);
    if (!localPhone || localPhone.length < 8) {
      setPhoneError('Please provide a valid phone number');
      return;
    }
    try {
      setBusy('send');
      const send = httpsCallable(functions, 'sendPhoneVerificationCode');
      const result = await send({ phone: formatPhone(localPhone) });
      const data = result.data as { success?: boolean; message?: string };
      setCodeSent(true);
      setPhoneVerified(false);
      setCode('');
      setInfo(data.message || 'Verification code sent. Check your phone.');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to send code.';
      setPhoneError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setBusy('idle');
    }
  };

  const handleVerifyCode = async () => {
    setPhoneError(null);
    setInfo(null);
    if (!/^\d{6}$/.test(code.trim())) {
      setPhoneError('Enter the 6-digit code from the SMS.');
      return;
    }
    try {
      setBusy('verify');
      const verify = httpsCallable(functions, 'verifyPhoneCode');
      const result = await verify({ code: code.trim() });
      const data = result.data as { success?: boolean; message?: string; phone?: string };
      setPhoneVerified(true);
      setLocalSmsEnabled(true);
      setCodeSent(false);
      setCode('');
      if (data.phone) setLocalPhone(data.phone.replace(/^\+/, ''));
      setInfo(data.message || 'Phone verified. SMS alerts enabled.');
      await refreshUser?.();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Verification failed.';
      setPhoneError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setBusy('idle');
    }
  };

  const handleSave = () => {
    setPhoneError(null);
    if (localSmsEnabled) {
      if (!localPhone || localPhone.length < 8) {
        setPhoneError('Please provide a valid phone number');
        return;
      }
      if (!phoneVerified) {
        setPhoneError('Verify your phone with the SMS code before saving.');
        return;
      }
    }
    onSave(localSmsEnabled, localPhone);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-dark-950/95 flex items-center justify-center z-[100000]">
      <div className="bg-dark-900 rounded-2xl p-8 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white uppercase">NOTIFICATIONS</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">
            ×
          </button>
        </div>

        <div className="space-y-5">
          {/* Email — always enabled */}
          <div className="flex items-start space-x-3">
            <div className="mt-0.5 w-5 h-5 flex items-center justify-center rounded border-2 border-emerald-500 bg-emerald-500 flex-shrink-0">
              <svg
                className="w-3 h-3 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-white text-lg">Email notifications enabled</p>
              <p className="text-gray-400 text-sm">{userEmail}</p>
            </div>
          </div>

          {/* SMS + OTP (Alice mock) */}
          <div className="flex flex-col space-y-3">
            <label className="flex items-start space-x-3 cursor-pointer">
              <div
                onClick={() => {
                  setLocalSmsEnabled(v => !v);
                  setPhoneError(null);
                  setInfo(null);
                }}
                className="mt-0.5 w-5 h-5 flex items-center justify-center rounded border-2 flex-shrink-0 transition-colors"
                style={{
                  borderColor: '#92d050',
                  backgroundColor: localSmsEnabled ? '#92d050' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                {localSmsEnabled && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
              <div className="text-left">
                <p className="text-white text-lg">
                  SMS alerts (faster)
                  {phoneVerified && localSmsEnabled ? (
                    <span className="ml-2 text-xs text-emerald-400 font-medium">Verified</span>
                  ) : null}
                </p>
                <p className="text-gray-400 text-sm">
                  Be immediately notified when a new startup matches your preferences.
                </p>
                <p className="text-gray-400 text-sm">Pause anytime</p>
              </div>
            </label>

            {localSmsEnabled && (
              <div className="ml-8 space-y-3 text-left">
                <PhoneInput
                  country={phoneCountry}
                  value={localPhone}
                  onChange={value => {
                    setLocalPhone(value);
                    if (phoneVerified) {
                      setPhoneVerified(false);
                      setCodeSent(false);
                    }
                    setPhoneError(null);
                    setInfo(null);
                  }}
                  inputProps={{ id: 'express-phone', name: 'phone', autoComplete: 'tel' }}
                  countryCodeEditable={false}
                  enableLongNumbers={true}
                  containerClass="w-full"
                  inputClass="!w-full"
                  inputStyle={{
                    color: 'black',
                    width: '100%',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                    paddingTop: '0.5rem',
                    paddingBottom: '0.5rem',
                    paddingRight: '0.75rem',
                  }}
                  dropdownStyle={{ color: 'black', textAlign: 'left' }}
                  buttonStyle={{ paddingLeft: '8px', paddingRight: '6px' }}
                />

                {!phoneVerified && (
                  <>
                    <button
                      type="button"
                      onClick={handleSendCode}
                      disabled={busy !== 'idle'}
                      className="w-full px-4 py-3 rounded-lg bg-black text-white text-sm font-semibold border border-gray-600 hover:bg-gray-900 disabled:opacity-50"
                    >
                      {busy === 'send' ? 'Sending…' : 'Send Verification Code'}
                    </button>

                    <p className="text-xs text-gray-400 text-left">
                      Didn&apos;t receive an SMS?{' '}
                      <button
                        type="button"
                        onClick={handleSendCode}
                        disabled={busy !== 'idle'}
                        className="underline hover:text-gray-300 disabled:opacity-50"
                      >
                        Click here
                      </button>
                    </p>

                    {codeSent && (
                      <div className="space-y-2">
                        <label
                          className="block text-sm text-white text-left"
                          htmlFor="express-sms-otp"
                        >
                          Enter the 6-digit code
                        </label>
                        <input
                          id="express-sms-otp"
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={code}
                          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="w-full max-w-[220px] rounded-md px-3 py-2 text-gray-900 text-lg tracking-widest bg-[#F2F2F2]"
                          placeholder="••••••"
                        />
                        <button
                          type="button"
                          onClick={handleVerifyCode}
                          disabled={busy !== 'idle' || code.length !== 6}
                          className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50"
                        >
                          {busy === 'verify' ? 'Verifying…' : 'Verify code'}
                        </button>
                      </div>
                    )}
                  </>
                )}

                {phoneError && <p className="text-red-500 text-sm text-left">{phoneError}</p>}
                {info && <p className="text-emerald-400 text-sm text-left">{info}</p>}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-4 mt-8">
          <button
            onClick={onClose}
            className="px-6 py-2 text-white border border-gray-600 rounded-lg hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={busy !== 'idle'}
            className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

/** Same choices as the investor profile Founders step. Empty means no preference. */
const FOUNDER_REPRESENTATION_OPTIONS = [
  { name: 'Woman', value: 'woman' },
  { name: 'POC (person of color)', value: 'poc' },
  { name: 'LGBTQ+', value: 'lgbtq' },
  { name: 'Immigrant', value: 'immigrant' },
  { name: 'Person with disabilities', value: 'personWithDisabilities' },
  { name: 'Senior/65+ entrepreneur', value: 'seniorEntrepreneur' },
  { name: 'Teen/19- entrepreneur', value: 'teenEntrepreneur' },
];

const BulletRow: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center space-x-2">
    <div className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0"></div>
    <span className="text-xs text-left text-emerald-400">{label}</span>
  </div>
);

/** Empty required-field hint — grey by default, red after a failed save attempt. */
const RequiredFieldHint: React.FC<{ error?: boolean }> = ({ error }) => (
  <p className={`text-xs italic text-left ${error ? 'text-red-500' : 'text-gray-500'}`}>
    Required field
  </p>
);

const ExpressSignUp: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { investorProfile, createProfile, updateProfile, loading: profileLoading } = useProfile();
  const navigate = useNavigate();

  // Filter out placeholder options
  const industriesOptions = industrySelectOptions.filter(opt => opt.value !== '');
  const productFocusOptions = productTypeSelectOptions.filter(opt => opt.value !== '');
  const stagesOptions = stageSelectOptions.filter(opt => opt.value !== '');

  // Account details (prefilled from signup; editable)
  const [accountName, setAccountName] = useState('');
  const [accountCompanyName, setAccountCompanyName] = useState('');
  // Email comes from the pricing page signup before the account record is available
  const [accountEmail, setAccountEmail] = useState(
    () => loadPricingSignupDraft('investor')?.email ?? ''
  );
  const [basedCountry, setBasedCountry] = useState('');
  const [basedState, setBasedState] = useState('');
  const [basedCity, setBasedCity] = useState('');

  // State for all preferences (defaults to "any" / empty arrays)
  const [industries, setIndustries] = useState<string[]>([]);
  const [otherIndustry, setOtherIndustry] = useState('');
  const [businessModelPreference, setBusinessModelPreference] = useState<string[]>([]);
  const [productFocusPreference, setProductFocusPreference] = useState<string[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [founderRepresentation, setFounderRepresentation] = useState<string[]>([]);
  const [investmentRange, setInvestmentRange] = useState<[number, number]>([0, 0]);
  const [isRangeEmpty, setIsRangeEmpty] = useState<boolean>(true);
  const [currency, setCurrency] = useState<string>('eur');
  const [countries, setCountries] = useState<string[]>([]);
  const [previousInvestments, setPreviousInvestments] = useState('');
  const [isMultiplierActive, setIsMultiplierActive] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  /** True after user tries to save — empty required fields turn red */
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Modal states
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isBusinessModelModalOpen, setIsBusinessModelModalOpen] = useState(false);
  const [isProductFocusModalOpen, setIsProductFocusModalOpen] = useState(false);
  const [isStagesModalOpen, setIsStagesModalOpen] = useState(false);
  const [isFounderRepresentationModalOpen, setIsFounderRepresentationModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isSuccessFeeModalOpen, setIsSuccessFeeModalOpen] = useState(false);
  const [paysFee, setPaysFee] = useState<boolean | null>(null);

  // Notification state
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [notifPhone, setNotifPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('us');
  /** True after opening the notifications popup (Save, Cancel, or ×) */
  const [hasReviewedNotifications, setHasReviewedNotifications] = useState(false);
  /** True only when Save was used — persists SMS prefs on final submit */
  const [hasInteractedWithNotifications, setHasInteractedWithNotifications] = useState(false);

  // Track if we've already initialized from profile
  const [hasInitialized, setHasInitialized] = useState(false);
  const boundAccountId = useRef<string | null>(null);

  const clearAnotherAccount = () => {
    setAccountName('');
    setAccountCompanyName('');
    setAccountEmail(loadPricingSignupDraft('investor')?.email ?? '');
    setBasedCountry('');
    setBasedState('');
    setBasedCity('');
    setIndustries([]);
    setOtherIndustry('');
    setBusinessModelPreference([]);
    setProductFocusPreference([]);
    setStages([]);
    setFounderRepresentation([]);
    setInvestmentRange([0, 0]);
    setIsRangeEmpty(true);
    setCurrency('eur');
    setCountries([]);
    setPreviousInvestments('');
    setIsMultiplierActive(false);
    setPaysFee(null);
    setHasInitialized(false);
  };

  // Signing up while another investor is still signed in used to keep that person's
  // name, company, and preferences in this form until a full remount.
  useEffect(() => {
    const uid = expressContextUid(user?.id, auth.currentUser?.uid ?? null);
    const action = expressAccountBindAction(boundAccountId.current, uid);
    if (action === 'ignore') return;
    boundAccountId.current = uid;
    if (action === 'clear') clearAnotherAccount();
  }, [user]);

  // Fetch existing preferences when investorProfile exists
  useEffect(() => {
    const firebaseUid = auth.currentUser?.uid ?? null;
    if (
      investorProfile &&
      user &&
      investorProfileBelongsToAccount(investorProfile.userId, user.id, firebaseUid) &&
      !hasInitialized &&
      !profileLoading
    ) {
      // Fetch industries
      const loadedIndustries =
        investorProfile.industries &&
        Array.isArray(investorProfile.industries) &&
        investorProfile.industries.length > 0
          ? canonicalIndustryList([...investorProfile.industries])
          : [];
      const loadedOtherIndustry =
        typeof investorProfile.otherIndustry === 'string'
          ? investorProfile.otherIndustry.trim()
          : '';
      if (loadedOtherIndustry && !loadedIndustries.includes(INDUSTRY_OTHER_VALUE)) {
        loadedIndustries.push(INDUSTRY_OTHER_VALUE);
      }
      // Union with any modal picks made before profile hydrate (same race as IndustryStep).
      if (loadedIndustries.length > 0) {
        setIndustries(prev => unionStringLists(loadedIndustries, prev));
      }
      if (loadedOtherIndustry) {
        setOtherIndustry(prev => prev.trim() || loadedOtherIndustry);
      }

      // Fetch business model preference
      if (
        investorProfile.businessModelPreference &&
        Array.isArray(investorProfile.businessModelPreference) &&
        investorProfile.businessModelPreference.length > 0
      ) {
        setBusinessModelPreference(prev =>
          unionStringLists(investorProfile.businessModelPreference, prev)
        );
      }

      // Fetch product focus preference
      if (
        investorProfile.productFocusPreference &&
        Array.isArray(investorProfile.productFocusPreference) &&
        investorProfile.productFocusPreference.length > 0
      ) {
        setProductFocusPreference(prev =>
          unionStringLists(investorProfile.productFocusPreference, prev)
        );
      }

      // Fetch stages
      if (
        investorProfile.stages &&
        Array.isArray(investorProfile.stages) &&
        investorProfile.stages.length > 0
      ) {
        setStages(prev => unionStringLists(investorProfile.stages, prev));
      }

      // Fetch currency
      if (investorProfile.currency) {
        setCurrency(investorProfile.currency);
      }

      // Fetch investment range (always true amounts).
      if (investorProfile.investmentRange && Array.isArray(investorProfile.investmentRange)) {
        const [min, max] = investorProfile.investmentRange;
        const multiplierWasActive = investorProfile.investmentMultiplierActive ?? false;
        const lowCeiling = MAX_INVESTMENT_AMOUNT_USD / 10;
        setIsMultiplierActive(multiplierWasActive || min > lowCeiling || max > lowCeiling);
        const loadedRange: [number, number] = [min, max];
        setInvestmentRange(loadedRange);
        if (investmentRangeIsSaveable(loadedRange)) {
          setIsRangeEmpty(false);
        }
      }

      // Fetch countries
      if (
        investorProfile.countries &&
        Array.isArray(investorProfile.countries) &&
        investorProfile.countries.length > 0
      ) {
        setCountries(prev => unionStringLists(investorProfile.countries, prev));
      }

      if (typeof investorProfile.previousInvestments === 'string') {
        setPreviousInvestments(investorProfile.previousInvestments);
      }

      if (Array.isArray(investorProfile.founderRepresentation)) {
        const known = new Set(FOUNDER_REPRESENTATION_OPTIONS.map(option => option.value));
        setFounderRepresentation(
          investorProfile.founderRepresentation.filter(
            (value): value is string => typeof value === 'string' && known.has(value)
          )
        );
      }

      // Fetch paysFee
      if (investorProfile.paysFee !== undefined) {
        setPaysFee(investorProfile.paysFee);
      }

      setHasInitialized(true);
    }
  }, [investorProfile, hasInitialized, profileLoading, user]);

  // Prefill account details from signup / existing profile
  useEffect(() => {
    if (!user) return;
    const firebaseUid = auth.currentUser?.uid ?? null;
    if (firebaseUid && firebaseUid !== user.id) return;
    if (
      investorProfile &&
      !investorProfileBelongsToAccount(investorProfile.userId, user.id, firebaseUid)
    ) {
      return;
    }
    const ownProfile = investorProfileBelongsToAccount(
      investorProfile?.userId,
      user.id,
      firebaseUid
    )
      ? investorProfile
      : null;
    setAccountName(prev => prev || user.name || '');
    setAccountCompanyName(prev => prev || ownProfile?.companyName || '');
    setAccountEmail(prev => prev || user.email || '');
    setBasedCountry(prev => prev || ownProfile?.country || user.country || '');
    setBasedState(prev => prev || ownProfile?.state || '');
    setBasedCity(prev => prev || ownProfile?.city || '');
  }, [user, investorProfile]);

  // Initialize notification preferences from existing user data
  useEffect(() => {
    if (user) {
      setSmsEnabled((user.notificationPreferences as any)?.smsNotifications || false);
      setNotifPhone((user as any)?.phone || '');
    }
  }, [user]);

  // Phone dial code follows the investor's based country (signup/profile), not login IP.
  useEffect(() => {
    const based = basedCountry || investorProfile?.country || user?.country || '';
    const iso2 = countryToIso2(based);
    if (iso2) {
      setPhoneCountry(iso2.toLowerCase());
    }
  }, [basedCountry, investorProfile?.country, user?.country]);

  // Format display text functions
  const getIndustryLabel = (value: string): string => {
    if (value === INDUSTRY_OTHER_VALUE) {
      return otherIndustry.trim() || 'Other';
    }
    return getDisplayName(INDUSTRY_DISPLAY_MAP, value as keyof typeof INDUSTRY_DISPLAY_MAP);
  };

  const standardIndustryCount = industries.filter(value => value !== INDUSTRY_OTHER_VALUE).length;
  const industriesIncomplete =
    industries.length === 0 || (industries.includes(INDUSTRY_OTHER_VALUE) && !otherIndustry.trim());

  const getIndustriesDisplay = (): string => {
    if (
      standardIndustryCount === industriesOptions.length &&
      !industries.includes(INDUSTRY_OTHER_VALUE)
    ) {
      return 'Any industry';
    }
    if (industries.length === 1) {
      return getIndustryLabel(industries[0]);
    }
    return industries.map(getIndustryLabel).join(', ');
  };

  const getBusinessModelDisplay = (): string => {
    // Check if "b2b-b2c" is selected
    if (businessModelPreference.includes('b2b-b2c')) {
      return getDisplayName(
        BUSINESS_MODEL_DISPLAY_MAP,
        'b2b-b2c' as keyof typeof BUSINESS_MODEL_DISPLAY_MAP
      );
    }
    // If both b2b and b2c are selected individually, show "B2B and B2C"
    if (businessModelPreference.includes('b2b') && businessModelPreference.includes('b2c')) {
      return getDisplayName(
        BUSINESS_MODEL_DISPLAY_MAP,
        'b2b-b2c' as keyof typeof BUSINESS_MODEL_DISPLAY_MAP
      );
    }
    // Single selection
    if (businessModelPreference.length === 1) {
      return getDisplayName(
        BUSINESS_MODEL_DISPLAY_MAP,
        businessModelPreference[0] as keyof typeof BUSINESS_MODEL_DISPLAY_MAP
      );
    }
    // Multiple individual selections (shouldn't happen with new logic, but handle it)
    return businessModelPreference
      .map(bm =>
        getDisplayName(BUSINESS_MODEL_DISPLAY_MAP, bm as keyof typeof BUSINESS_MODEL_DISPLAY_MAP)
      )
      .join(', ');
  };

  const getProductFocusDisplay = (): string => {
    if (productFocusPreference.length === productFocusOptions.length) {
      return 'Any';
    }
    if (productFocusPreference.length === 1) {
      return getDisplayName(
        PRODUCT_FOCUS_DISPLAY_MAP,
        productFocusPreference[0] as keyof typeof PRODUCT_FOCUS_DISPLAY_MAP
      );
    }
    return productFocusPreference
      .map(pf =>
        getDisplayName(PRODUCT_FOCUS_DISPLAY_MAP, pf as keyof typeof PRODUCT_FOCUS_DISPLAY_MAP)
      )
      .join(', ');
  };

  const getStagesDisplay = (): string => {
    if (stages.length === stagesOptions.length) {
      return 'Any stage';
    }
    if (stages.length === 1) {
      return getDisplayName(STAGE_DISPLAY_MAP, stages[0] as keyof typeof STAGE_DISPLAY_MAP);
    }
    return stages
      .map(stage => getDisplayName(STAGE_DISPLAY_MAP, stage as keyof typeof STAGE_DISPLAY_MAP))
      .join(', ');
  };

  const getLocationDisplay = (): string => {
    const concrete = countries.filter(country => country !== 'ANYWHERE');
    if (concrete.length === 1) return concrete[0];
    if (concrete.length > 1) return concrete.join(', ');
    if (countries.includes('ANYWHERE')) return 'Worldwide';
    return '';
  };

  const formatPhoneForSaving = (phoneNumber: string): string => {
    if (!phoneNumber) return '';
    return phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
  };

  const handleSavePreferences = async () => {
    setAttemptedSave(true);

    const trimmedName = accountName.trim();
    const trimmedEmail = accountEmail.trim().toLowerCase();

    if (!trimmedName) {
      setValidationError('Please enter your name.');
      return;
    }
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setValidationError('Please enter a valid email address.');
      return;
    }
    if (!basedCountry) {
      setValidationError('Please select the country where you are based.');
      return;
    }
    if (!basedCity.trim()) {
      setValidationError('Please enter your city.');
      return;
    }
    if (countryRequiresStateSubdivision(basedCountry) && !basedState.trim()) {
      setValidationError(
        isUnitedKingdomCountry(basedCountry)
          ? 'Please select your county.'
          : 'Please select your state.'
      );
      return;
    }

    const previousInvestmentsMissing = !previousInvestments.trim();
    const rangeFloor = investorInvestmentRangeFloor(currency);
    const investmentRangeMissing =
      isRangeEmpty || !investmentRangeIsSaveable(investmentRange, rangeFloor);
    const missingRequired =
      industriesIncomplete ||
      businessModelPreference.length === 0 ||
      productFocusPreference.length === 0 ||
      stages.length === 0 ||
      countries.length === 0 ||
      previousInvestmentsMissing ||
      investmentRangeMissing ||
      (SUCCESS_FEE_LIVE && !isUnitedStatesCountry(basedCountry) && paysFee === null) ||
      !hasReviewedNotifications;

    if (missingRequired) {
      // Field-level "Required field" hints turn red; keep a short summary for the CTA
      setValidationError('Please fill in all required fields.');
      return;
    }

    setValidationError(null);

    if (!user) {
      console.error('User not found');
      return;
    }

    setIsSaving(true);
    try {
      // Persist account details (name / email / based country) before preferences
      const updateUserFn = httpsCallable(functions, 'updateUser');
      const userUpdateResult = await updateUserFn({
        userId: user.id,
        updates: {
          name: trimmedName,
          email: trimmedEmail,
          country: basedCountry,
        },
      });
      const userUpdateData = userUpdateResult.data as { success?: boolean; error?: string };
      if (!userUpdateData?.success) {
        setValidationError(userUpdateData?.error || 'Failed to update your account details.');
        setIsSaving(false);
        return;
      }
      await refreshUser();

      // Prepare profile data (range is already true amounts; cap at ~200M USD in local currency)
      const cap = maxInvestmentInCurrency(currency || 'usd', null, MAX_INVESTMENT_AMOUNT_USD);
      const finalInvestmentRange: [number, number] = [
        Math.min(cap, investmentRange[0]),
        Math.min(cap, investmentRange[1]),
      ];

      const profileData = {
        ...(investorProfile || {}),
        // Basic Information
        companyName: accountCompanyName.trim(),
        email: trimmedEmail,
        country: basedCountry,
        city: basedCity.trim(),
        ...(countryRequiresStateSubdivision(basedCountry)
          ? { state: basedState.trim() }
          : { state: '' }),

        // Investment Preferences (from Express Sign Up)
        industries,
        otherIndustry: industries.includes(INDUSTRY_OTHER_VALUE) ? otherIndustry.trim() : '',
        businessModelPreference,
        productFocusPreference,
        stages,
        founderRepresentation,
        currency: currency as 'eur' | 'usd' | 'gbp' | 'sek',
        investmentRange: finalInvestmentRange,
        investmentMultiplierActive: isMultiplierActive,

        // Geographic Focus
        countries: countries,

        previousInvestments: previousInvestments.trim(),

        // Success Fee Agreement (non-US investors only)
        ...(SUCCESS_FEE_LIVE && !isUnitedStatesCountry(basedCountry) && paysFee !== null
          ? { paysFee, hasAcceptedFeeAgreement: true }
          : {}),

        // Profile Completion Status
        // investAs is only on the full profile. Leave it unset here so express signup
        // does not mark equity as chosen.
        currentStep: investorProfile?.currentStep || 0,
        lastCompletedStep: investorProfile?.lastCompletedStep ?? -1,
        expressSignupCompletedAt: new Date().toISOString(),
      };

      // Save profile
      if (investorProfile) {
        await updateProfile(user.id, 'investor', profileData, 0);
      } else {
        await createProfile(user.id, 'investor', profileData);
      }

      // Save notification preferences if the user interacted with that section
      // (SMS section stays on the form; this only persists prefs when edited)
      if (hasInteractedWithNotifications) {
        const updateNotifPrefs = httpsCallable(functions, 'updateNotificationPreferences');
        await updateNotifPrefs({
          smsNotifications: smsEnabled,
          phone: smsEnabled ? formatPhoneForSaving(notifPhone) : undefined,
        });
      }

      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (profileLoading && !hasInitialized) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading preferences...</p>
        </div>
      </div>
    );
  }

  const cityMissing = !basedCity.trim();
  const stateMissing = countryRequiresStateSubdivision(basedCountry) && !basedState.trim();
  const rangeFloor = investorInvestmentRangeFloor(currency);
  const investmentRangeMissing =
    isRangeEmpty || !investmentRangeIsSaveable(investmentRange, rangeFloor);
  const cityFieldError = attemptedSave && cityMissing;
  const stateFieldError = attemptedSave && stateMissing;
  return (
    <div className="min-h-screen bg-dark-950 py-8">
      <div className="container mx-auto px-4 max-w-2xl ml-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-gray-300 uppercase mb-4">
            WHAT STARTUPS DO YOU WANT TO SEE?
          </h1>
          <p className="text-lg text-gray-400">Takes just 2 minutes</p>
        </div>

        {/* Account details — prefilled from signup, editable */}
        <div className="px-8 pb-2 space-y-4">
          <div>
            <label
              htmlFor="express-account-name"
              className="block text-sm font-medium text-gray-300 mb-1 text-left"
            >
              Name
            </label>
            <input
              id="express-account-name"
              type="text"
              value={accountName}
              onChange={e => {
                setAccountName(e.target.value);
                setValidationError(null);
              }}
              autoComplete="name"
              className="w-full px-3 py-2 rounded-md bg-[#F2F2F2] text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label
              htmlFor="express-account-company-name"
              className="block text-sm font-medium text-gray-300 mb-1 text-left"
            >
              Company Name (if no company, leave it blank)
            </label>
            <input
              id="express-account-company-name"
              type="text"
              value={accountCompanyName}
              onChange={e => {
                setAccountCompanyName(e.target.value);
                setValidationError(null);
              }}
              autoComplete="organization"
              className="w-full px-3 py-2 rounded-md bg-[#F2F2F2] text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label
              htmlFor="express-account-email"
              className="block text-sm font-medium text-gray-300 mb-1 text-left"
            >
              Email
            </label>
            <input
              id="express-account-email"
              type="email"
              value={accountEmail}
              onChange={e => {
                setAccountEmail(e.target.value);
                setValidationError(null);
              }}
              autoComplete="email"
              className="w-full px-3 py-2 rounded-md bg-[#F2F2F2] text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label
              htmlFor="express-based-country"
              className="block text-sm font-medium text-gray-300 mb-1 text-left"
            >
              Country where you are officially headquartered.
            </label>
            <CountrySelect
              id="express-based-country"
              value={basedCountry}
              onChange={nextCountry => {
                setBasedCountry(nextCountry);
                setBasedCity('');
                if (
                  !countryRequiresStateSubdivision(nextCountry) ||
                  isUnitedStatesCountry(basedCountry) !== isUnitedStatesCountry(nextCountry) ||
                  isUnitedKingdomCountry(basedCountry) !== isUnitedKingdomCountry(nextCountry)
                ) {
                  setBasedState('');
                }
                setValidationError(null);
              }}
              className="w-full px-3 py-2 rounded-md bg-[#F2F2F2] text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none"
            />
          </div>
          {isUnitedStatesCountry(basedCountry) && (
            <div>
              <label
                htmlFor="express-based-state"
                className={`block text-sm font-medium mb-1 text-left ${
                  stateFieldError ? 'text-red-500' : 'text-gray-300'
                }`}
              >
                State *
              </label>
              <select
                id="express-based-state"
                value={basedState}
                onChange={e => {
                  setBasedState(e.target.value);
                  setValidationError(null);
                }}
                className={`w-full px-3 py-2 rounded-md bg-[#F2F2F2] text-gray-900 border focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none ${
                  stateFieldError ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select state</option>
                {usStates.map(state => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              {stateFieldError ? (
                <p className="mt-1 text-left text-sm text-red-500">Please select your state.</p>
              ) : null}
            </div>
          )}
          {isUnitedKingdomCountry(basedCountry) && (
            <div>
              <label
                htmlFor="express-based-state"
                className={`block text-sm font-medium mb-1 text-left ${
                  stateFieldError ? 'text-red-500' : 'text-gray-300'
                }`}
              >
                County *
              </label>
              <select
                id="express-based-state"
                value={basedState}
                onChange={e => {
                  setBasedState(e.target.value);
                  setValidationError(null);
                }}
                className={`w-full px-3 py-2 rounded-md bg-[#F2F2F2] text-gray-900 border focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none ${
                  stateFieldError ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select county</option>
                {UK_COUNTIES.map(county => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
              {stateFieldError ? (
                <p className="mt-1 text-left text-sm text-red-500">Please select your county.</p>
              ) : null}
            </div>
          )}
          {basedCountry ? (
            <div>
              <label
                htmlFor="express-based-city"
                className={`block text-sm font-medium mb-1 text-left ${
                  cityFieldError ? 'text-red-500' : 'text-gray-300'
                }`}
              >
                City *
              </label>
              <CitySelect
                id="express-based-city"
                country={basedCountry}
                region={basedState}
                value={basedCity}
                required
                onChange={city => {
                  setBasedCity(city);
                  setValidationError(null);
                }}
                className={`w-full px-3 py-2 rounded-md bg-[#F2F2F2] text-gray-900 border focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  cityFieldError ? 'border-red-500' : 'border-gray-300'
                }`}
                error={cityFieldError}
              />
              {cityFieldError ? (
                <p className="mt-1 text-left text-sm text-red-500">Please select your city.</p>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Preferences Sections - All in one box */}
        <div className="p-8 space-y-8">
          {/* INDUSTRIES Section */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1 min-w-0">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <img src={industryIcon} alt="Industries" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">
                  INDUSTRIES
                </h3>
                {industriesIncomplete ? (
                  <RequiredFieldHint error={attemptedSave} />
                ) : industries.length > 1 &&
                  !(
                    standardIndustryCount === industriesOptions.length &&
                    !industries.includes(INDUSTRY_OTHER_VALUE)
                  ) ? (
                  <div className="space-y-1">
                    {industries.map(ind => (
                      <BulletRow key={ind} label={getIndustryLabel(ind)} />
                    ))}
                  </div>
                ) : (
                  <BulletRow label={getIndustriesDisplay()} />
                )}
              </div>
            </div>
            <button
              onClick={() => setIsIndustriesModalOpen(true)}
              className={
                industriesIncomplete
                  ? 'border border-emerald-500 text-emerald-500 hover:border-emerald-400 hover:text-emerald-400 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
                  : 'border border-gray-500 text-gray-400 hover:border-gray-300 hover:text-gray-300 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
              }
            >
              {industriesIncomplete ? 'Select' : 'Edit'}
            </button>
          </div>

          {/* BUSINESS MODEL PREFERENCE Section */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1 min-w-0">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <img
                  src={bizModelIcon}
                  alt="Business Model"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">
                  BUSINESS MODEL PREFERENCE
                </h3>
                {businessModelPreference.length === 0 ? (
                  <RequiredFieldHint error={attemptedSave} />
                ) : (
                  <BulletRow label={getBusinessModelDisplay()} />
                )}
              </div>
            </div>
            <button
              onClick={() => setIsBusinessModelModalOpen(true)}
              className={
                businessModelPreference.length === 0
                  ? 'border border-emerald-500 text-emerald-500 hover:border-emerald-400 hover:text-emerald-400 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
                  : 'border border-gray-500 text-gray-400 hover:border-gray-300 hover:text-gray-300 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
              }
            >
              {businessModelPreference.length === 0 ? 'Select' : 'Edit'}
            </button>
          </div>

          {/* PRODUCT FOCUS PREFERENCE Section */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1 min-w-0">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <img
                  src={productIcon}
                  alt="Product Focus"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">
                  PRODUCT FOCUS PREFERENCE
                </h3>
                {productFocusPreference.length === 0 ? (
                  <RequiredFieldHint error={attemptedSave} />
                ) : productFocusPreference.length > 1 &&
                  productFocusPreference.length !== productFocusOptions.length ? (
                  <div className="space-y-1">
                    {productFocusPreference.map(pf => (
                      <BulletRow
                        key={pf}
                        label={getDisplayName(
                          PRODUCT_FOCUS_DISPLAY_MAP,
                          pf as keyof typeof PRODUCT_FOCUS_DISPLAY_MAP
                        )}
                      />
                    ))}
                  </div>
                ) : (
                  <BulletRow label={getProductFocusDisplay()} />
                )}
              </div>
            </div>
            <button
              onClick={() => setIsProductFocusModalOpen(true)}
              className={
                productFocusPreference.length === 0
                  ? 'border border-emerald-500 text-emerald-500 hover:border-emerald-400 hover:text-emerald-400 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
                  : 'border border-gray-500 text-gray-400 hover:border-gray-300 hover:text-gray-300 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
              }
            >
              {productFocusPreference.length === 0 ? 'Select' : 'Edit'}
            </button>
          </div>

          {/* STAGES Section */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1 min-w-0">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <img src={stageIcon} alt="Stages" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">STAGES</h3>
                {stages.length === 0 ? (
                  <RequiredFieldHint error={attemptedSave} />
                ) : stages.length > 1 && stages.length !== stagesOptions.length ? (
                  <div className="space-y-1">
                    {stages.map(stage => (
                      <BulletRow
                        key={stage}
                        label={getDisplayName(
                          STAGE_DISPLAY_MAP,
                          stage as keyof typeof STAGE_DISPLAY_MAP
                        )}
                      />
                    ))}
                  </div>
                ) : (
                  <BulletRow label={getStagesDisplay()} />
                )}
              </div>
            </div>
            <button
              onClick={() => setIsStagesModalOpen(true)}
              className={
                stages.length === 0
                  ? 'border border-emerald-500 text-emerald-500 hover:border-emerald-400 hover:text-emerald-400 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
                  : 'border border-gray-500 text-gray-400 hover:border-gray-300 hover:text-gray-300 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
              }
            >
              {stages.length === 0 ? 'Select' : 'Edit'}
            </button>
          </div>

          {/* FOUNDER REPRESENTATION Section — optional; empty means no preference */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1 min-w-0">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <img
                  src={foundersIcon}
                  alt="Founder representation"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">
                  FOUNDER REPRESENTATION
                </h3>
                <p className="text-gray-400 text-sm mb-2 text-left">
                  Feel free to select any that may apply.
                </p>
                {founderRepresentation.length === 0 ? (
                  <p className="text-xs italic text-left text-gray-500">No preference</p>
                ) : (
                  <div className="space-y-1">
                    {founderRepresentation.map(value => {
                      const option = FOUNDER_REPRESENTATION_OPTIONS.find(
                        item => item.value === value
                      );
                      return <BulletRow key={value} label={option?.name || value} />;
                    })}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsFounderRepresentationModalOpen(true)}
              className={
                founderRepresentation.length === 0
                  ? 'border border-emerald-500 text-emerald-500 hover:border-emerald-400 hover:text-emerald-400 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
                  : 'border border-gray-500 text-gray-400 hover:border-gray-300 hover:text-gray-300 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
              }
            >
              {founderRepresentation.length === 0 ? 'Select' : 'Edit'}
            </button>
          </div>

          {/* INVESTMENT RANGE Section */}
          <div>
            <div className="flex items-start space-x-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <img
                  src={amountIcon}
                  alt="Investment Range"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">
                  INVESTMENT RANGE *
                </h3>
                {investmentRangeMissing && <RequiredFieldHint error={attemptedSave} />}
              </div>
            </div>
            <div className="max-w-md">
              <RangeSelector
                currency={currency}
                range={investmentRange}
                onCurrencyChange={cur => {
                  setCurrency(cur);
                  setValidationError(null);
                }}
                onRangeChange={range => {
                  setInvestmentRange(range);
                  setIsRangeEmpty(false);
                  setValidationError(null);
                }}
                multiplier={10}
                isMultiplierActive={isMultiplierActive}
                onMultiplierToggle={active => {
                  setIsMultiplierActive(active);
                  setValidationError(null);
                }}
                hideRangeText={false}
                min={MIN_INVESTMENT_AMOUNT}
                max={MAX_INVESTMENT_AMOUNT_USD}
                step={10000}
                scaleExponent={0.4}
                isEmpty={isRangeEmpty}
              />
              {attemptedSave && investmentRangeMissing ? (
                <p className="mt-2 text-left text-sm text-red-500">
                  {isRangeEmpty || !investmentRangeIsSaveable(investmentRange)
                    ? 'Please fill in your investment amount.'
                    : `Minimum investment: ${rangeFloor.toLocaleString()} ${(currency || 'usd').toUpperCase()}`}
                </p>
              ) : null}
            </div>
          </div>

          {/* LOCATION Section */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1 min-w-0">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <img src={locationIcon} alt="Location" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">LOCATION</h3>
                <p className="text-gray-400 text-sm mb-2 text-left">
                  Where would you prefer the startups to be located?
                </p>
                {countries.filter(country => country !== 'ANYWHERE').length === 0 &&
                !countries.includes('ANYWHERE') ? (
                  <RequiredFieldHint error={attemptedSave} />
                ) : countries.filter(country => country !== 'ANYWHERE').length > 1 ? (
                  <div className="space-y-1">
                    {countries
                      .filter(country => country !== 'ANYWHERE')
                      .map(country => (
                        <BulletRow key={country} label={country} />
                      ))}
                  </div>
                ) : (
                  <BulletRow label={getLocationDisplay()} />
                )}
              </div>
            </div>
            <button
              onClick={() => setIsLocationModalOpen(true)}
              className={
                countries.length === 0
                  ? 'border border-emerald-500 text-emerald-500 hover:border-emerald-400 hover:text-emerald-400 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
                  : 'border border-gray-500 text-gray-400 hover:border-gray-300 hover:text-gray-300 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
              }
            >
              {countries.length === 0 ? 'Select' : 'Edit'}
            </button>
          </div>

          {/* PREVIOUS STARTUP INVESTMENTS — required; n/a is a valid answer */}
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <img
                src={handshakeIcon}
                alt="Previous startup investments"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">
                PREVIOUS STARTUP INVESTMENTS
              </h3>
              <p className="text-gray-400 text-sm mb-2 text-left">
                By you or your firm. If none, type n/a
              </p>
              <input
                type="text"
                name="previousInvestments"
                value={previousInvestments}
                onChange={event => {
                  setPreviousInvestments(event.target.value);
                  if (event.target.value.trim()) setValidationError(null);
                }}
                placeholder="Describe your previous investments..."
                className={`mt-2 w-full max-w-md p-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
                  attemptedSave && !previousInvestments.trim()
                    ? 'border-red-500'
                    : 'border-gray-300'
                }`}
              />
            </div>
          </div>

          {/* SUCCESS FEE Section (non-US investors only). Hidden while SUCCESS_FEE_LIVE is false. */}
          {SUCCESS_FEE_LIVE && !isUnitedStatesCountry(basedCountry) && (
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1 min-w-0">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <img
                    src={trophyIcon}
                    alt="Success Fee"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">
                    SUCCESS FEE
                  </h3>
                  {paysFee === null ? (
                    <RequiredFieldHint error={attemptedSave} />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="text-emerald-400 text-xs">★</span>
                      <span className="text-xs text-left text-emerald-400">
                        {paysFee
                          ? 'Separately by us (Startup-Friendly Investor)'
                          : "From the startup's allocation"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setIsSuccessFeeModalOpen(true)}
                className={
                  paysFee === null
                    ? 'border border-emerald-500 text-emerald-500 hover:border-emerald-400 hover:text-emerald-400 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
                    : 'border border-gray-500 text-gray-400 hover:border-gray-300 hover:text-gray-300 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
                }
              >
                {paysFee === null ? 'Select' : 'Edit'}
              </button>
            </div>
          )}
          {/* NOTIFICATIONS Section — required; email prefilled in popup; Cancel/× skips SMS */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1 min-w-0">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold uppercase text-white mb-2 text-left">
                  NOTIFICATIONS
                </h3>
                {!hasReviewedNotifications ? (
                  <RequiredFieldHint error={attemptedSave} />
                ) : (
                  <div className="space-y-1">
                    <BulletRow label="Email notifications" />
                    {smsEnabled && <BulletRow label="SMS alerts" />}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsNotificationsModalOpen(true)}
              className={
                !hasReviewedNotifications
                  ? 'border border-emerald-500 text-emerald-500 hover:border-emerald-400 hover:text-emerald-400 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
                  : 'border border-gray-500 text-gray-400 hover:border-gray-300 hover:text-gray-300 rounded-full px-4 py-1 text-sm flex-shrink-0 ml-4 w-20 text-center'
              }
            >
              {!hasReviewedNotifications ? 'Select' : 'Edit'}
            </button>
          </div>
        </div>

        {/* OK Button */}
        <div className="mt-12 text-center">
          {validationError && (
            <div className="mb-4">
              <p className="text-red-500 text-sm">{validationError}</p>
            </div>
          )}
          <button
            onClick={handleSavePreferences}
            disabled={isSaving || showSuccessPopup}
            className="inline-flex items-center justify-center px-8 py-3 hover:bg-emerald-800 text-white font-medium rounded-lg border-2 border-emerald-700 bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <span
                  className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                  aria-hidden
                />
                Saving…
              </>
            ) : (
              'Go Explore Startups'
            )}
          </button>
        </div>

        {showSuccessPopup && (
          <ExpressSignupSuccessPopup onContinue={() => navigate('/dashboard')} />
        )}

        {/* Modals */}
        <IndustriesModal
          isOpen={isIndustriesModalOpen}
          onClose={() => setIsIndustriesModalOpen(false)}
          selectedValues={industries}
          otherIndustry={otherIndustry}
          onSave={(values, otherText) => {
            setIndustries(values);
            setOtherIndustry(otherText);
            const valid =
              values.length > 0 &&
              (!values.includes(INDUSTRY_OTHER_VALUE) || otherText.trim().length > 0);
            if (valid) setValidationError(null);
          }}
          options={industriesOptions}
        />
        <BusinessModelModal
          isOpen={isBusinessModelModalOpen}
          onClose={() => setIsBusinessModelModalOpen(false)}
          selectedValues={businessModelPreference}
          onSave={values => {
            setBusinessModelPreference(values);
            if (values.length > 0) setValidationError(null);
          }}
        />
        <CheckboxModal
          isOpen={isProductFocusModalOpen}
          onClose={() => setIsProductFocusModalOpen(false)}
          selectedValues={productFocusPreference}
          onSave={values => {
            setProductFocusPreference(values);
            if (values.length > 0) setValidationError(null);
          }}
          title="PRODUCT FOCUS PREFERENCE"
          options={productFocusOptions}
        />
        <CheckboxModal
          isOpen={isStagesModalOpen}
          onClose={() => setIsStagesModalOpen(false)}
          selectedValues={stages}
          onSave={values => {
            setStages(values);
            if (values.length > 0) setValidationError(null);
          }}
          title="STAGES"
          options={stagesOptions}
        />
        <CheckboxModal
          isOpen={isFounderRepresentationModalOpen}
          onClose={() => setIsFounderRepresentationModalOpen(false)}
          selectedValues={founderRepresentation}
          onSave={values => {
            setFounderRepresentation(values);
          }}
          title="FOUNDER REPRESENTATION"
          options={FOUNDER_REPRESENTATION_OPTIONS}
          allowEmpty
        />
        <LocationModal
          isOpen={isLocationModalOpen}
          onClose={() => setIsLocationModalOpen(false)}
          selectedCountries={countries}
          onSave={selected => {
            setCountries(selected);
            if (selected.length > 0) setValidationError(null);
          }}
        />
        {SUCCESS_FEE_LIVE ? (
          <SuccessFeeModal
            isOpen={isSuccessFeeModalOpen}
            onClose={() => setIsSuccessFeeModalOpen(false)}
            currentValue={paysFee}
            onSave={value => {
              setPaysFee(value);
              if (value !== null) setValidationError(null);
            }}
          />
        ) : null}
        <NotificationsModal
          isOpen={isNotificationsModalOpen}
          onClose={() => {
            // Cancel / × still completes the required step (email stays on; SMS optional)
            setIsNotificationsModalOpen(false);
            setHasReviewedNotifications(true);
            setValidationError(null);
            // Refresh so verified OTP (already persisted) shows on the summary row
            void refreshUser?.();
          }}
          userEmail={accountEmail || user?.email || ''}
          initialSmsEnabled={smsEnabled}
          initialPhone={notifPhone}
          phoneCountry={phoneCountry}
          onSave={(sms, phone) => {
            setSmsEnabled(sms);
            setNotifPhone(phone);
            setHasReviewedNotifications(true);
            setHasInteractedWithNotifications(true);
            setValidationError(null);
          }}
        />
      </div>
    </div>
  );
};

export default ExpressSignUp;
